import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { LOGO_URL } from "@/config";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Obnovenie hesla | Sedlo Services";
    const t = new URLSearchParams(window.location.search).get("token") || "";
    setToken(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Nové heslo musí mať aspoň 8 znakov."); return; }
    if (password !== confirm) { setError("Heslá sa nezhodujú."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success("Heslo bolo úspešne zmenené.");
      setTimeout(() => navigate("/admin"), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || "Obnovenie hesla zlyhalo.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#060a12] px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-2xl" data-testid="reset-password-card">
        <img src={LOGO_URL} alt="Sedlo Services" className="h-12 w-auto mx-auto mb-6" />
        {done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h1 className="font-head text-xl font-semibold text-navy mb-2">Heslo zmenené</h1>
            <p className="text-sm text-slate-500">Presmerúvame vás na prihlásenie…</p>
          </div>
        ) : !token ? (
          <div className="text-center" data-testid="reset-invalid">
            <h1 className="font-head text-xl font-semibold text-navy mb-2">Neplatný odkaz</h1>
            <p className="text-sm text-slate-500 mb-6">Odkaz na obnovenie hesla je neúplný alebo neplatný. Požiadajte o nový.</p>
            <Link to="/admin" className="text-sm text-brand hover:underline">Späť na prihlásenie</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-password-form">
            <div className="flex items-center justify-center gap-2 text-navy mb-6">
              <ShieldCheck className="w-5 h-5 text-brand" />
              <h1 className="font-head text-xl font-semibold">Nastavenie nového hesla</h1>
            </div>
            <label className="block text-sm font-medium text-navy mb-1">Nové heslo</label>
            <input data-testid="reset-new-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full mb-4 px-4 py-2.5 rounded-md border border-navy/15 focus:outline-none focus:ring-2 focus:ring-brand" />
            <label className="block text-sm font-medium text-navy mb-1">Zopakujte nové heslo</label>
            <input data-testid="reset-confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
              className="w-full mb-2 px-4 py-2.5 rounded-md border border-navy/15 focus:outline-none focus:ring-2 focus:ring-brand" />
            <p className="text-xs text-slate-400 mb-4">Heslo musí mať aspoň 8 znakov.</p>
            {error && <p data-testid="reset-error" className="text-sm text-red-600 mb-4">{error}</p>}
            <button data-testid="reset-submit" disabled={loading}
              className="btn-lift w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-3 rounded-md disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Nastaviť nové heslo"}
            </button>
            <Link to="/admin" className="mt-5 flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-brand">
              <ArrowLeft className="w-4 h-4" /> Späť na prihlásenie
            </Link>
          </form>
        )}
      </div>
      <Toaster position="top-center" />
    </div>
  );
}
